// src/components/forms/mapping-editor/ObjectFieldMappingEditor.tsx (NEW - Renders inputs for ONE field mapping)
import React, { useCallback, useMemo } from 'react';
import { ObjectFieldMapping, ObjectFieldMappingConditionConfig, ObjectMapping } from '../../../../interfaces/dynamicForms/PdfMapping';
import { SimpleListEditor } from './SimpleListEditor';
import FormInput from '../formBuilder/FormInput';
import { FormDefinition, getAllQuestionIds } from '../../../../interfaces/dynamicForms/FormBuilder';

// Define the known shapes of dynamic source objects.
// In a real application, this might come from a shared configuration or API.
const DYNAMIC_SOURCE_SHAPES: Record<string, string[]> = {
    'participants': [
        'id',
        'firstName',
        'lastName',
        'fullName',
        'caseNumber',
        'dateOfBirth',
        'email',
        'phoneNumber', 
        'waiver.waiverName'
    ], 
    'taskContactTypes': [
        'Name'
    ],
    'waiverType': [
        'Name'
    ]
};


interface ObjectFieldMappingEditorProps {
    item: ObjectFieldMapping;
    index: number; // Index within the Fields array
    onChange: (index: number, updatedItem: ObjectFieldMapping) => void; // Callback to update this specific item
    sourceFieldOptions: string[]; // New prop for dynamic source fields
}

const ObjectFieldMappingEditor: React.FC<ObjectFieldMappingEditorProps> = ({ item, index, onChange, sourceFieldOptions }) => {

    const handleInputChange = (field: keyof Omit<ObjectFieldMapping, 'Condition'>, value: string) => {
        onChange(index, { ...item, [field]: value });
    };

    const handleConditionChange = (field: keyof ObjectFieldMappingConditionConfig, value: string) => {
         const updatedCondition: ObjectFieldMappingConditionConfig = {
            ...(item.Condition ?? { Field: '', Value: '' }), // Use existing or default
            [field]: value,
         };
         // Set condition to null if both fields are empty, otherwise set the object
         const newCondition = (!updatedCondition.Field && !updatedCondition.Value) ? undefined : updatedCondition;
         onChange(index, { ...item, Condition: newCondition });
    };

    const toggleCondition = () => {
         onChange(index, { ...item, Condition: item.Condition ? undefined : { Field: '', Value: '' } });
    };

    return (
        <div className="border-l-4 pl-3 ml-1 border-gray-300 space-y-3">
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label htmlFor={`obj-field-source-${index}`} className="block text-sm font-medium text-gray-700 mb-1">Source Field (within Object)</label>
                    <select
                        id={`obj-field-source-${index}`}
                        value={item.SourceField || ''}
                        onChange={(e) => handleInputChange('SourceField', e.target.value)}
                        className="block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                        required
                    >
                        <option value="">Select property...</option>
                        {sourceFieldOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>
                 <FormInput
                     label="Target PDF Field"
                     id={`obj-field-pdf-${index}`}
                     value={item.PdfField || ''}
                     onChange={(e) => handleInputChange('PdfField', e.target.value)}
                     placeholder="e.g., TextField1"
                     required
                  />
             </div>
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                 <FormInput
                      label="Combine With Field (Optional)"
                      id={`obj-field-combine-${index}`}
                      value={item.CombineWith || ''}
                      onChange={(e) => handleInputChange('CombineWith', e.target.value)}
                      placeholder="e.g., lastName"
                  />
                 <FormInput
                      label="Separator (if Combining)"
                      id={`obj-field-sep-${index}`}
                      value={item.Separator || ''}
                      onChange={(e) => handleInputChange('Separator', e.target.value)}
                      placeholder="Default: space"
                  />
             </div>
             {/* Condition Section */}
             <div className="mt-2 border-t pt-2">
                 <label className="flex items-center space-x-2 cursor-pointer text-sm mb-1">
                    <input
                        type="checkbox"
                        checked={!!item.Condition}
                        onChange={toggleCondition}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="font-medium text-gray-700">Apply Condition?</span>
                </label>
                 {item.Condition && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6">
                         <FormInput
                              label="Condition Form Field ID"
                              id={`obj-field-cond-field-${index}`}
                              value={item.Condition.Field || ''}
                              onChange={(e) => handleConditionChange('Field', e.target.value)}
                              placeholder="e.g., someOtherField"
                          />
                         <FormInput
                              label="Required Value"
                              id={`obj-field-cond-value-${index}`}
                              value={item.Condition.Value || ''}
                              onChange={(e) => handleConditionChange('Value', e.target.value)}
                              placeholder="e.g., Yes"
                          />
                    </div>
                 )}
             </div>
        </div>
    );
};

interface ObjectMappingsEditorProps {
  mappings: ObjectMapping[] | undefined;
  updateMappings: (newMappings: ObjectMapping[]) => void;
  formDefinition: FormDefinition | null;
}

const ObjectMappingsEditor: React.FC<ObjectMappingsEditorProps> = ({ mappings, updateMappings, formDefinition }) => {

  const dynamicSourceQuestions = useMemo(() => {
    if (!formDefinition) return [];
    // Get all questions, then filter for those with a dynamicSource
    return formDefinition.steps.flatMap(step => step.questions).filter(q => !!q.dynamicSource);
  }, [formDefinition]);


  // Function to render the inputs for a single ObjectMapping item
  const renderObjectMappingItem = useCallback((
    item: ObjectMapping,
    index: number,
    onChange: (index: number, updatedItem: ObjectMapping) => void
  ) => {
    const handleObjectNameChange = (value: string) => {
      onChange(index, { ...item, ObjectName: value, Fields: [] }); // Reset fields when object changes
    };

    // Callback for the nested SimpleListEditor (for Fields)
    const handleFieldsChange = (newFields: ObjectFieldMapping[]) => {
       onChange(index, { ...item, Fields: newFields });
    };

    const selectedQuestion = dynamicSourceQuestions.find(q => q.id === item.ObjectName);
    const sourceFieldOptions = selectedQuestion?.dynamicSource
        ? DYNAMIC_SOURCE_SHAPES[selectedQuestion.dynamicSource] || []
        : [];


     // Function to render ONE ObjectFieldMapping using its dedicated editor
     const renderFieldMappingItem = (
         fieldItem: ObjectFieldMapping,
         fieldIndex: number,
         onFieldChange: (fieldIndex: number, updatedFieldItem: ObjectFieldMapping) => void
     ) => {
         return (
             <ObjectFieldMappingEditor
                 item={fieldItem}
                 index={fieldIndex}
                 onChange={onFieldChange} // Pass the change handler down
                 sourceFieldOptions={sourceFieldOptions}
             />
         );
     };

      // Function to create a default ObjectFieldMapping
      const createDefaultFieldMappingItem = (): ObjectFieldMapping => ({
          SourceField: '', PdfField: '', CombineWith: undefined, Separator: undefined, Condition: undefined
      });

    return (
      <div className="space-y-4">
        <div>
            <label htmlFor={`objmap-name-${index}`} className="block text-sm font-medium text-gray-700 mb-1">Object Name in Form Data</label>
            <select
                id={`objmap-name-${index}`}
                value={item.ObjectName || ''}
                onChange={(e) => handleObjectNameChange(e.target.value)}
                className="block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                required
            >
                <option value="">Select a dynamic source field...</option>
                {dynamicSourceQuestions.map(q => (
                    <option key={q.id} value={q.id}>{q.id} ({q.dynamicSource})</option>
                ))}
            </select>
        </div>

         {/* Nested Editor for the Fields within this ObjectMapping */}
         <div className="pl-4">
            <SimpleListEditor<ObjectFieldMapping>
                items={item.Fields}
                updateItems={handleFieldsChange} // Updates the 'Fields' array of the current 'item'
                renderItem={renderFieldMappingItem} // Uses the specific field mapping renderer
                createDefaultItem={createDefaultFieldMappingItem} // Uses the specific field mapping creator
                title="Field Mappings within Object"
                itemTitle="Field Mapping"
                addButtonText="Add Field Mapping"
             />
         </div>
      </div>
    );
  }, [dynamicSourceQuestions]);

  // Function to create a new, empty ObjectMapping item
  const createDefaultObjectMappingItem = useCallback((): ObjectMapping => {
    return { ObjectName: '', Fields: [] }; // Start with empty fields array
  }, []);

  return (
    <SimpleListEditor<ObjectMapping>
      items={mappings}
      updateItems={updateMappings}
      renderItem={renderObjectMappingItem}
      createDefaultItem={createDefaultObjectMappingItem}
      title="Object Mappings"
      itemTitle="Object Mapping Rule"
      addButtonText="Add Object Mapping Rule"
    />
  );
};

export default ObjectMappingsEditor;